import React, { useEffect, useState } from 'react';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import AddEditPublisherModal from '../components/publisher/AddEditPublisherModal';
import { getPublishers } from '../services/publisherApi';
import { Publisher } from '../types/publisher';
import ImportPublisherModal from '../components/publisher/ImportPublisherModal';
import { useScopes } from '../../../hooks/useScopes';
import { ApiScope } from '../../../types/api-scopes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ExternalLink, Pencil, Image, Plus } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const Index = (props: any) => {
    const { userProfile, hasScope } = useScopes();
    const [data, setData] = useState<Publisher[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Publisher | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditPublisherModalOpen, setIsAddOrderModalOpen] = useState<boolean>(false);
    const [isImportPublisherModalOpen, setIsImportPublisherModalOpen] = useState<boolean>(false);

    useEffect(() => {
        // Lista os publishers cadastrados na tabela
        getPublishers().then((res) => {
            setData(res);
            setLoading(false);
        });
    }, []);

    const handleUpdateData = (newData: Publisher) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Publisher | Publisher[]) => {
        if (Array.isArray(newData)) {
            setData([...data, ...newData]);
        } else {
            setData([...data, newData]);
        }
    };

    const toggleAddEditPublisherModal = () => {
        setIsAddOrderModalOpen(!isAddEditPublisherModalOpen);
    };

    const toggleImportPublisherModal = () => {
        setIsImportPublisherModalOpen(!isImportPublisherModalOpen);
    }

    const columns = [
        {
            header: 'Mídia Cad',
            cell: (cell: any) => {
                return (
                    <span className="font-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'mediaCad',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Ad Network',
            accessorFn: (row: any) => {
                return row.adNetwork?.name ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Site / App',
            accessorFn: (row: any) => {
                return row.site || row.app;
            },
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.site) {
                    return (<span>{row.site} <a href={'https://' + row.site} target="_blank" rel="noreferrer" className="text-blue-600"><ExternalLink className="h-4 w-4 inline" /></a></span>);
                }

                if (row.app) {
                    return row.app;
                }

                return '-';
            },
            enableColumnFilter: false,
        },
        {
            header: 'TAGs',
            accessorFn: (row: any) => {
                return row.tags?.length || 0;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Campanhas',
            accessorFn: (row: any) => {
                const campaignsCountSet = new Set();
                if (row.orders && row.orders.length > 0) {
                    row.orders.forEach((order: any) => {
                        campaignsCountSet.add(order.campaign.id);
                    });
                }
                return campaignsCountSet.size;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Blocos de anúncios',
            accessorFn: (row: any) => {
                return row.placements?.length || 0;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Usuários',
            accessorFn: (row: any) => {
                return row.users?.length || 0;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                // Midia Kit
                if (row.mediaKitUrl || row.mediaKitFile) {
                    actionCell.push(<TooltipProvider key="mediaKit">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <a href={row.mediaKitUrl || row.mediaKitFile}
                                   rel={'noreferrer'}
                                   target="_blank"
                                   className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
                                >
                                    <Image className="h-4 w-4" />
                                </a>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Mídia Kit</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>);
                }

                // Botão de editar
                if (hasScope(ApiScope.PUBLISHER_WRITE) || userProfile?.publisher?.id === row.id) {
                    actionCell.push(<TooltipProvider key="edit">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white shadow hover:bg-green-700 h-9 px-4 py-2"
                                        onClick={() => {
                                            setDataToEdit(row);
                                            toggleAddEditPublisherModal();
                                        }}
                                ><Pencil className="h-4 w-4" /></button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Editar</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>);
                }

                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Publishers | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Publishers</CardTitle>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => {
                                                    setDataToEdit(null);
                                                    toggleAddEditPublisherModal();
                                                }}
                                                className="flex items-center gap-2"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Adicionar publisher
                                            </Button>

                                            <Button
                                                onClick={toggleImportPublisherModal}
                                                variant="outline"
                                                className="flex items-center gap-2"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Importar publishers
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {!loading ? <TableContainer
                                                columns={(columns || [])}
                                                data={(data || [])}
                                                isGlobalFilter={true}
                                                customPageSize={30}
                                                SearchPlaceholder="Procurar..."
                                            /> :
                                        <div className="text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                                        </div>}

                                    <AddEditPublisherModal
                                        publisher={dataToEdit}
                                        isOpen={isAddEditPublisherModalOpen}
                                        toggle={toggleAddEditPublisherModal}
                                        onAddData={handleAddData}
                                        onUpdateData={handleUpdateData}
                                    />

                                    <ImportPublisherModal
                                        isOpen={isImportPublisherModalOpen}
                                        toggle={toggleImportPublisherModal}
                                        onAddData={handleAddData}
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
